import {
  ACTIONS_LIST_TYPE,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
} from "@miplatform/constants";
import { toKebabCase } from "@miplatform/utils";
import { MiButton } from "../MiButton/MiButton";

/**
 * Renders a list of action buttons or icons.
 * @param {Object} props - The component props.
 * @param {("BUTTONS"|"ICONS")} [props.type=ACTIONS_LIST_TYPE.BUTTONS] - The type of action list.
 * @param {Array} [props.actions=[]] - The list of actions.
 * @param {string} props.actions[].name - The name of the action.
 * @param {string} props.actions[].title - The title of the action. (name can be used if title is not provided)
 * @param {Function} props.actions[].onClick - The function to call on click.
 * @param {boolean} [props.actions[].disabled] - Whether the button is disabled.
 * @param {Object} [props.actions[].style] - Custom styles for the button.
 * @param {string} [props.actions[].size] - The size of the button.
 * @param {string} [props.actions[].color] - The color of the button, ex: error, success, info, primary.
 * @param {string} [props.actions[].className] - Custom class name for the button.
 * @param {Object} [props.actions[].startIcon] - The start icon.
 * @param {Object} [props.actions[].endIcon] - The end icon.
 * @param {Object} [props.actions[].description] - The description for the action.
 * @param {Object} [props.actions[].icon] - The icon for `ICONS` type.
 * @returns {JSX.Element[]} The rendered action buttons or icons.
 */
export const MiActions = ({
  type = ACTIONS_LIST_TYPE.BUTTONS,
  actions = [],
  buttonProps,
}) =>
  actions?.map((action) => {
    const title =
      type !== ACTIONS_LIST_TYPE.ICONS &&
      action.variant === BUTTON_VARIANTS.TEXT ? (
        <span style={{ textDecoration: "underline", textUnderlineOffset: 4 }}>
          {action.title || action.name}
        </span>
      ) : (
        action.title || action.name
      );

    return (
      <MiButton
        key={action.title || action.name}
        type={action.type}
        data-testid={`${toKebabCase(action.name)}-btn`}
        {...(type === ACTIONS_LIST_TYPE.ICONS
          ? {
              title: "",
              startIcon: action.startIcon || {
                path: action.icon,
              },
              color: action.color || BUTTON_COLORS.GREY,
              variant: action.variant || BUTTON_VARIANTS.TEXT,
            }
          : {
              title,
              variant: action.variant,
              color: action.color,
              startIcon: action.startIcon,
            })}
        onClick={action.onClick}
        disabled={action.disabled}
        style={action.style}
        size={action.size}
        className={action.className}
        endIcon={action.endIcon}
        description={action.description}
        width={action.width}
        {...buttonProps}
      />
    );
  });
